import { useState } from 'react'
import axios from 'axios'

import { API_BASE_URL } from '../config/api'
import { buildEventDays, formatDateForInput, spansMultipleDays } from '../utils/eventDateUtils'
import { describeTicketmasterEvent } from '../utils/ticketmasterUtils'

const TICKETMASTER_PREF_KEY = 'mainstage:includeTicketmaster'

export const useEventSearch = (onEventSelect) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [selectedFlyer, setSelectedFlyer] = useState(null)
  const [flyerPreview, setFlyerPreview] = useState(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [ticketmasterResults, setTicketmasterResults] = useState([])
  const [searchSource, setSearchSource] = useState('local')

  // Whether to search Ticketmaster alongside your own events. Remembered
  // across visits because it is a standing preference, not a per-search one.
  const [includeTicketmaster, setIncludeTicketmasterState] = useState(() => {
    try {
      return window.localStorage.getItem(TICKETMASTER_PREF_KEY) !== 'false'
    } catch (e) {
      // Private browsing / storage disabled — fall back to the default.
      return true
    }
  })

  const setIncludeTicketmaster = (value) => {
    setIncludeTicketmasterState(value)
    try {
      window.localStorage.setItem(TICKETMASTER_PREF_KEY, String(value))
    } catch (e) {
      // Preference just won't persist; not worth surfacing.
    }
    // Drop results already on screen so the list matches the toggle at once.
    if (!value) setTicketmasterResults([])
  }

  const searchEvents = async () => {
    if (!searchQuery.trim() && !selectedFlyer) return

    try {
      setSearching(true)

      if (selectedFlyer) {
        // Handle flyer upload and processing
        const formData = new FormData()
        formData.append('flyer', selectedFlyer)

        const response = await axios.post(`${API_BASE_URL}/api/events/extract-from-flyer`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        })

        if (response.data) {
          onEventSelect(response.data)
          // Clear the flyer after successful extraction
          setSelectedFlyer(null)
          setFlyerPreview(null)
        }
      } else {
        // Ticketmaster is skipped entirely when the toggle is off, rather than
        // fetched and filtered out. Each call spends a slot on a rate-limited
        // third-party key, so not asking is the point.
        const requests = [
          axios.get(`${API_BASE_URL}/api/events/search?q=${encodeURIComponent(searchQuery)}`, {
            withCredentials: true
          })
        ]

        if (includeTicketmaster) {
          requests.push(
            axios.get(`${API_BASE_URL}/api/events/ticketmaster/search?q=${encodeURIComponent(searchQuery)}`, {
              withCredentials: true
            })
          )
        }

        const [localResponse, ticketmasterResponse] = await Promise.allSettled(requests)

        setSearchResults(
          localResponse.status === 'fulfilled' ? (localResponse.value.data.events || []) : []
        )

        setTicketmasterResults(
          ticketmasterResponse?.status === 'fulfilled'
            ? (ticketmasterResponse.value.data.events || [])
            : []
        )
      }
    } catch (error) {
      // Search error - fail silently
    } finally {
      setSearching(false)
    }
  }

  const handleFlyerUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      setSelectedFlyer(file)

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => setFlyerPreview(e.target.result)
      reader.readAsDataURL(file)

      // Clear search query
      setSearchQuery('')
    }
  }

  const removeFlyerUpload = () => {
    setSelectedFlyer(null)
    setFlyerPreview(null)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)

    const files = e.dataTransfer.files
    if (files.length > 0) {
      const file = files[0]
      if (file.type.startsWith('image/') || file.type === 'application/pdf') {
        setSelectedFlyer(file)

        // Create preview
        const reader = new FileReader()
        reader.onload = (e) => setFlyerPreview(e.target.result)
        reader.readAsDataURL(file)

        // Clear search query
        setSearchQuery('')
      }
    }
  }

  const handleSearchQueryChange = (value) => {
    setSearchQuery(value)
    if (value && selectedFlyer) {
      removeFlyerUpload()
    }
  }

  const handleEventSelect = (event) => {
    // Only copy essential template fields, not user-specific data
    // This prevents _id conflicts and avoids copying personal customizations
    const eventTemplate = {
      // Basic event info
      name: event.name || '',
      description: event.description || '',
      eventType: event.eventType || 'festival',

      // Dates
      startDate: formatDateForInput(event.startDate),
      endDate: formatDateForInput(event.endDate),
      startTime: event.startTime || '',
      endTime: event.endTime || '',
      timeTBA: event.timeTBA || false,
      endTimeTBA: event.endTimeTBA || false,
      dateTBA: event.dateTBA || false,
      noSpecificTime: event.noSpecificTime || false,
      timezone: event.timezone || '',
      // Derived, not copied: the source has no spanMultipleDays field, so
      // copying it always produced an unchecked box on multi-day festivals.
      spanMultipleDays: spansMultipleDays(event.startDate, event.endDate),

      // Location
      venueName: event.venueName || '',
      venueAddress: event.venueAddress || '',
      country: event.country || '',

      // Lineup (just the text, not individual artist records)
      // Only kept when there are no structured artists to show as bubbles —
      // otherwise the same names appear twice, once as bubbles and once as a
      // run-on string in "General lineup".
      lineup: (event.artistsSeen?.length || event.officialLineup?.length) ? '' : (event.lineup || ''),

      // Artists - only copy basic info, no user customizations
      artistsSeen: event.artistsSeen?.map(artist => ({
        name: artist.name || '',
        genre: artist.genre || '',
        confirmed: true,
        seen: false, // Always false for new events
        favorite: false,
        performanceDate: formatDateForInput(event.startDate) || '',
        setTime: '',
        performance: '',
        comment: ''
      })) || [],

      // Default values for user-specific fields (not copied from template)
      attended: false,
      wasStreamed: false,
      attendanceType: 'none',
      attendedDates: [],
      additionalDates: [],
      rating: null,
      notes: '',
      favoritePerformance: '',
      top10: false,
      memorableMoment: false,
      momentCaption: '',
      notableDetails: '',
      privacy: 'public'
    }

    onEventSelect(eventTemplate)
    setSearchResults([])
    setTicketmasterResults([])
    setSearchQuery('')
  }

  const handleTicketmasterEventSelect = (ticketmasterEvent) => {
    // Transform Ticketmaster event to local event format
    const artists = ticketmasterEvent.artists || []

    const localEvent = {
      name: ticketmasterEvent.name,
      startDate: ticketmasterEvent.date,
      endDate: ticketmasterEvent.endDate || ticketmasterEvent.date,
      startTime: ticketmasterEvent.time || '',
      endTime: ticketmasterEvent.endTime || '',
      timeTBA: ticketmasterEvent.timeTBA || false,
      endTimeTBA: ticketmasterEvent.endTimeTBA || false,
      dateTBA: ticketmasterEvent.dateTBA || false,
      noSpecificTime: ticketmasterEvent.noSpecificTime || false,
      timezone: ticketmasterEvent.timezone || '',
      // Ticketmaster states this directly; fall back to comparing the dates
      // when it doesn't, since a festival can carry an end date without the
      // flag. Deriving alone would have thrown away the authoritative answer.
      spanMultipleDays: !!ticketmasterEvent.spanMultipleDays || spansMultipleDays(
        ticketmasterEvent.date,
        ticketmasterEvent.endDate || ticketmasterEvent.date
      ),
      additionalDates: buildEventDays(ticketmasterEvent.date, ticketmasterEvent.endDate),
      venueName: ticketmasterEvent.venue?.name || '',
      venueAddress: ticketmasterEvent.venue?.address || '',
      country: ticketmasterEvent.venue?.country || '',
      // Left empty when the artists are structured: they render as bubbles from
      // artistsSeen, and filling this too showed every name a second time as
      // one unreadable space-joined string in "General lineup". Same rule the
      // flyer extraction already follows.
      lineup: '',
      artistsSeen: artists.length > 0 ? artists.map(artist => ({
        name: artist.name,
        genre: artist.genre || '',
        confirmed: true, // Confirmed since they're from the official lineup
        seen: false, // Not seen yet - user needs to mark them
        favorite: false,
        performanceDate: ticketmasterEvent.date || '',
        setTime: '',
        performance: '',
        comment: ''
      })) : [],
      description: describeTicketmasterEvent(ticketmasterEvent),
      ticketmasterData: ticketmasterEvent
    }

    onEventSelect(localEvent)
    setSearchResults([])
    setTicketmasterResults([])
    setSearchQuery('')
  }

  return {
    searchQuery,
    searchResults,
    ticketmasterResults,
    searchSource,
    searching,
    selectedFlyer,
    flyerPreview,
    isDragOver,
    onSearch: searchEvents,
    onFlyerUpload: handleFlyerUpload,
    onRemoveFlyerUpload: removeFlyerUpload,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
    onSearchQueryChange: handleSearchQueryChange,
    onEventSelect: handleEventSelect,
    onTicketmasterEventSelect: handleTicketmasterEventSelect,
    setSearchSource,
    includeTicketmaster,
    onIncludeTicketmasterChange: setIncludeTicketmaster
  }
}